/**
 * Agent Events Kiosk — Public live-event lead capture (Phase 27.43)
 *
 * Route: `/agentevents` (public — no auth required).
 *
 * Four-step flow:
 *   1. Lookup       — type-ahead name search → pick → enter NPN → verify
 *   2. Event list   — pick assigned event OR create a new "kiosk event"
 *   3. Lead capture — landing-page-style form with camera photo, safety
 *                     word, typed consent signature, optional appointment
 *   4. Success      — one-tap "Add another" (same event) OR change event
 *
 * Session: 30-min inactivity timeout — server returns 401 once the
 * `X-Kiosk-Session` HMAC expires, and the UI drops back to step 1.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import {
  User, Search, ArrowRight, LogOut, Camera, CheckCircle2, Loader2,
  Calendar, Plus, MapPin, Phone, Mail, Sparkles, ShieldCheck, X,
  ChevronRight, Lock, UserCheck, AlertTriangle, RefreshCw,
} from 'lucide-react';

const KR_LOGO = "https://customer-assets.emergentagent.com/job_35859c3c-f3c9-45c7-a4c9-9ec823ac8280/artifacts/a7ioyzyi_3%20copy.png";
const KR_LOGO_DARK = "/brand/kr-logo-dark.webp";

// Small numbered step pill for the verification hero (Find Name → Confirm NPN).
function StepPill({ n, label, active, done }) {
  return (
    <div className="flex items-center gap-2" data-testid={`verify-step-${n}`}>
      <span className={
        'w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold transition-colors ' +
        (done ? 'bg-[#f5c876] text-[#0a1f47]'
          : active ? 'bg-white text-[#0a1f47] ring-2 ring-[#f5c876]'
          : 'bg-white/10 text-slate-300 ring-1 ring-white/20')
      }>
        {done ? <CheckCircle2 size={14} /> : n}
      </span>
      <span className={'text-xs font-semibold ' + (active || done ? 'text-white' : 'text-slate-400')}>{label}</span>
    </div>
  );
}

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api/agent-events`;

// axios instance with kiosk session header
function useKioskApi(session) {
  return useMemo(() => {
    const inst = axios.create({ baseURL: API });
    inst.interceptors.request.use((cfg) => {
      if (session) cfg.headers['X-Kiosk-Session'] = session;
      return cfg;
    });
    return inst;
  }, [session]);
}

// ── Product options (mirrors Lead Landing Page) ────────────
const PRODUCT_OPTIONS = [
  'Medicare Advantage', 'Medicare Supplement', 'Prescription (Part D)',
  'ACA / Marketplace', 'Life Insurance', 'Final Expense',
  'Dental / Vision / Hearing', 'Other',
];

// ── Card wrapper for polish ─────────────────────────────────
function KioskCard({ children, className = '', ...rest }) {
  return (
    <div
      className={`bg-white rounded-2xl shadow-xl ring-1 ring-slate-200/70 ${className}`}
      {...rest}
    >{children}</div>
  );
}

function BrandHeader({ agentName, onLogout }) {
  return (
    <header
      className="rounded-2xl ring-1 ring-[#0f2547]/10 shadow-md px-4 py-3 flex items-center justify-between relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0f2547 0%, #14335f 55%, #0f2547 100%)' }}
    >
      <div className="absolute bottom-0 left-0 right-0 h-[2px]"
           style={{ background: 'linear-gradient(90deg, transparent 0%, #f5c14f 20%, #f5c14f 80%, transparent 100%)' }} />
      <div className="flex items-center gap-3">
        <img src={KR_LOGO} alt="KR Enterprise" className="h-9 w-auto object-contain" />
        <div className="hidden sm:block h-8 w-px bg-white/15" />
        <div className="flex flex-col leading-tight">
          <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#f5c14f]">
            KR Enterprise Group
          </span>
          <h1 className="text-base sm:text-lg font-heading font-bold text-white tracking-tight">
            Agent Events · Live Capture
          </h1>
        </div>
      </div>
      {agentName && (
        <div className="flex items-center gap-2 text-white/80">
          <span className="hidden sm:inline text-xs opacity-70">Signed in as</span>
          <span className="text-sm font-semibold">{agentName}</span>
          <button
            onClick={onLogout}
            className="ml-2 p-1.5 rounded-md hover:bg-white/10 text-white/70 hover:text-white transition-colors"
            data-testid="kiosk-logout"
            title="End kiosk session"
          >
            <LogOut size={16} />
          </button>
        </div>
      )}
    </header>
  );
}

// ── STEP 1 — Lookup ────────────────────────────────────────
function LookupStep({ onSignedIn }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [npn, setNpn] = useState('');
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const debRef = useRef();

  useEffect(() => {
    clearTimeout(debRef.current);
    if (!q || q.length < 2) { setResults([]); return; }
    debRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(`${API}/public/search`, { params: { q } });
        setResults(data.results || []);
      } catch { /* silent */ }
      finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(debRef.current);
  }, [q]);

  const verify = async () => {
    if (!selected || !npn.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/public/verify`, {
        agent_ref: selected.agent_ref, npn: npn.trim(),
      });
      onSignedIn({ ...data, npn: npn.trim(), agency_name: selected.agency_name });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not verify. Please check your NPN.');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-5" data-testid="lookup-step">
      {/* Premium navy + gold verification hero */}
      <div className="relative overflow-hidden rounded-2xl bg-[#0a1f47] text-white shadow-xl ring-1 ring-[#b8862d]/25">
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(600px 220px at 100% 0%, rgba(245,193,79,0.16), transparent 60%), radial-gradient(500px 240px at 0% 100%, rgba(245,193,79,0.08), transparent 60%)' }} />
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#f5c876] to-transparent" />
        <div className="relative px-6 sm:px-8 pt-7 pb-6 text-center">
          <img src={KR_LOGO_DARK} alt="KR Enterprise Group" className="h-14 sm:h-16 w-auto object-contain mx-auto" />
          <h1 className="mt-5 text-2xl sm:text-3xl font-heading font-bold tracking-tight">Agent Verification Portal</h1>
          <p className="mt-2 text-sm text-slate-300 inline-flex items-center justify-center gap-1.5">
            <ShieldCheck size={14} className="text-[#f5c876]" /> Secure access for authorized KR agents
          </p>

          {/* 2-step indicator: Find Name → Confirm NPN */}
          <div className="mt-6 flex items-center justify-center gap-3">
            <StepPill n={1} label="Find Name" active={!selected} done={!!selected} />
            <div className={'h-px w-8 sm:w-12 transition-colors ' + (selected ? 'bg-[#f5c876]' : 'bg-white/20')} />
            <StepPill n={2} label="Confirm NPN" active={!!selected} done={false} />
          </div>

          {/* Trust indicators */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] text-slate-300">
            <span className="inline-flex items-center gap-1.5"><Lock size={12} className="text-[#f5c876]" /> Secure Access</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} className="text-[#f5c876]" /> Private Information</span>
            <span className="inline-flex items-center gap-1.5"><UserCheck size={12} className="text-[#f5c876]" /> Authorized Agents Only</span>
          </div>
        </div>
      </div>

      {/* Refined Find Your Name / Confirm NPN card */}
      <KioskCard className="p-6 sm:p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-[#0f2547]/10 flex items-center justify-center">
          {selected ? <ShieldCheck className="text-[#0f2547]" size={20} /> : <Search className="text-[#0f2547]" size={20} />}
        </div>
        <div>
          <h2 className="text-lg font-heading font-bold text-[#0f2547]">{selected ? 'Confirm your NPN' : 'Find your name'}</h2>
          <p className="text-xs text-slate-500">{selected ? 'Enter your NPN to securely continue' : 'Type-ahead search · then confirm with your NPN'}</p>
        </div>
      </div>

      {!selected ? (
        <>
          <label className="block text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Agent Name</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Start typing your first or last name..."
              className="w-full pl-9 pr-3 py-3 rounded-lg ring-1 ring-slate-200 focus:ring-2 focus:ring-[#0f2547] focus:outline-none text-slate-800"
              data-testid="lookup-search"
              autoFocus
            />
            {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" size={16} />}
          </div>
          {results.length > 0 && (
            <ul className="mt-2 rounded-lg ring-1 ring-slate-200 divide-y divide-slate-100 max-h-72 overflow-auto" data-testid="lookup-results">
              {results.map((r) => (
                <li key={r.agent_ref}
                    onClick={() => setSelected(r)}
                    className="px-3 py-2.5 cursor-pointer hover:bg-slate-50 flex items-center justify-between transition-colors"
                    data-testid={`lookup-option-${r.agent_ref.slice(0, 8)}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-[#0f2547] truncate">{r.display}</div>
                    {r.agency_name && <div className="text-xs text-slate-500 truncate">{r.agency_name}</div>}
                  </div>
                  <ChevronRight size={16} className="text-slate-400 shrink-0" />
                </li>
              ))}
            </ul>
          )}
          {!searching && q.length >= 2 && results.length === 0 && (
            <p className="mt-3 text-xs text-slate-500 italic">No matches — try a different spelling.</p>
          )}
        </>
      ) : (
        <>
          <div className="rounded-lg bg-slate-50 p-3 mb-4 flex items-center justify-between" data-testid="lookup-selected">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Selected</div>
              <div className="font-semibold text-[#0f2547] truncate">{selected.display}</div>
              {selected.agency_name && <div className="text-xs text-slate-500 truncate">{selected.agency_name}</div>}
            </div>
            <button onClick={() => { setSelected(null); setNpn(''); }}
                    className="text-xs text-slate-500 hover:text-[#0f2547] underline"
                    data-testid="lookup-change">
              Change
            </button>
          </div>
          <label className="block text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Your NPN</label>
          <input
            value={npn}
            onChange={(e) => setNpn(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => e.key === 'Enter' && verify()}
            placeholder="Enter your NPN"
            inputMode="numeric"
            className="w-full px-3 py-3 rounded-lg ring-1 ring-slate-200 focus:ring-2 focus:ring-[#0f2547] focus:outline-none text-slate-800 tracking-widest tabular-nums"
            data-testid="lookup-npn"
            autoFocus
          />
          <button
            onClick={verify}
            disabled={loading || !npn.trim()}
            className="mt-4 w-full py-3 rounded-lg bg-[#0f2547] text-white font-semibold hover:bg-[#14335f] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            data-testid="lookup-verify"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : <>Continue <ArrowRight size={16} /></>}
          </button>
        </>
      )}
      </KioskCard>
    </div>
  );
}

// ── STEP 2 — Event list ────────────────────────────────────
function EventListStep({ session, agentRef, npn, agentName, canSms, onPicked, onLogout }) {
  const api = useKioskApi(session);
  const [events, setEvents] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ event_name: '', event_date: '', venue: '', city: '', state: '' });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/public/${agentRef}/events`, { params: { npn } });
      setEvents(data.events || []);
    } catch (err) {
      if (err?.response?.status === 401) onLogout(true);
      else toast.error('Failed to load events');
    }
  }, [api, agentRef, npn, onLogout]);

  useEffect(() => { load(); }, [load]);

  const submitNew = async () => {
    if (!form.event_name.trim() || !form.event_date) {
      toast.error('Event name and date are required'); return;
    }
    try {
      const { data } = await api.post(`/public/${agentRef}/events`, form, { params: { npn } });
      toast.success('Event created');
      onPicked(data);
    } catch (err) {
      if (err?.response?.status === 401) onLogout(true);
      else toast.error(err?.response?.data?.detail || 'Failed to create event');
    }
  };

  return (
    <div className="space-y-4">
      <BrandHeader agentName={agentName} onLogout={() => onLogout(false)} />
      <KioskCard className="p-5 sm:p-6" data-testid="event-list-step">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Calendar size={18} className="text-[#0f2547]" />
            <h2 className="text-lg font-heading font-bold text-[#0f2547]">Your events</h2>
          </div>
          <button
            onClick={() => setCreating((v) => !v)}
            className="text-xs font-semibold text-[#0f2547] hover:text-[#14335f] flex items-center gap-1"
            data-testid="event-new-toggle"
          >
            {creating ? <><X size={14} /> Cancel</> : <><Plus size={14} /> New event</>}
          </button>
        </div>
        {creating && (
          <div className="mb-4 p-4 rounded-lg bg-slate-50 ring-1 ring-slate-200 space-y-2" data-testid="event-create-form">
            <input placeholder="Event name*" value={form.event_name}
                   onChange={(e) => setForm({ ...form, event_name: e.target.value })}
                   className="w-full px-3 py-2 rounded-md ring-1 ring-slate-200 text-sm"
                   data-testid="new-event-name" />
            <input type="date" value={form.event_date}
                   onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                   className="w-full px-3 py-2 rounded-md ring-1 ring-slate-200 text-sm"
                   data-testid="new-event-date" />
            <input placeholder="Venue (optional)" value={form.venue}
                   onChange={(e) => setForm({ ...form, venue: e.target.value })}
                   className="w-full px-3 py-2 rounded-md ring-1 ring-slate-200 text-sm" />
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="City" value={form.city}
                     onChange={(e) => setForm({ ...form, city: e.target.value })}
                     className="px-3 py-2 rounded-md ring-1 ring-slate-200 text-sm" />
              <input placeholder="State" value={form.state} maxLength={2}
                     onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })}
                     className="px-3 py-2 rounded-md ring-1 ring-slate-200 text-sm" />
            </div>
            <button onClick={submitNew}
                    className="w-full py-2 rounded-md bg-[#0f2547] text-white font-semibold text-sm hover:bg-[#14335f]"
                    data-testid="new-event-submit">
              Create & start capturing
            </button>
          </div>
        )}
        {events === null ? (
          <div className="py-8 text-center text-slate-400"><Loader2 className="animate-spin inline" /></div>
        ) : events.length === 0 ? (
          <div className="py-6 text-center text-slate-500 text-sm">
            You don&apos;t have any assigned events in the next 30 days.<br />
            Use <span className="font-semibold text-[#0f2547]">&quot;New event&quot;</span> above to start capturing immediately.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="event-list">
            {events.map((e) => (
              <li key={`${e.kind}-${e.event_id}`}
                  onClick={() => onPicked(e)}
                  className="p-3 rounded-lg ring-1 ring-slate-200 hover:ring-[#0f2547]/40 hover:bg-slate-50 cursor-pointer flex items-center justify-between transition-all group"
                  data-testid={`event-item-${e.event_id}`}>
                <div className="min-w-0 flex-1 flex items-start gap-3">
                  <div className="flex flex-col items-center bg-[#f5c14f]/15 rounded-md px-2 py-1 text-[#8a6510] shrink-0 min-w-[46px]">
                    <span className="text-[9px] uppercase font-bold tracking-wide">
                      {e.date ? new Date(e.date).toLocaleDateString('en-US', { month: 'short' }) : '—'}
                    </span>
                    <span className="text-base font-bold leading-none">
                      {e.date ? new Date(e.date).getDate() : ''}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-[#0f2547] truncate">{e.name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                      <span className="capitalize">{e.kind}</span>
                      {e.venue && <><span>·</span><span className="truncate">{e.venue}</span></>}
                      {e.city && <><span>·</span><span>{e.city}{e.state ? `, ${e.state}` : ''}</span></>}
                      {e.source === 'kiosk' && <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#f5c14f]/20 text-[#8a6510] text-[9px] font-bold uppercase">Kiosk</span>}
                    </div>
                  </div>
                </div>
                <ChevronRight size={18} className="text-slate-400 group-hover:text-[#0f2547] shrink-0" />
              </li>
            ))}
          </ul>
        )}
        {canSms && (
          <div className="mt-4 flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 rounded-md px-3 py-2 ring-1 ring-emerald-200">
            <ShieldCheck size={14} />
            SMS lead outreach enabled on your account — you&apos;ll see an SMS toggle when capturing a lead.
          </div>
        )}
      </KioskCard>
    </div>
  );
}

// ── STEP 3 — Lead capture ──────────────────────────────────
function LeadFormStep({ session, agentRef, npn, agentName, canSms, event, onDone, onBack, onLogout }) {
  const api = useKioskApi(session);
  const [form, setForm] = useState({
    first_name: '', last_name: '', gender: '', phone: '', email: '',
    address: '', city: '', state: '', zip_code: '', county: '', product_of_interest: '',
    date_of_birth: '', notes: '', preferred_language: 'en',
    sms_opt_in: false, safety_word: '', consent_signature: '',
  });
  const [photoData, setPhotoData] = useState(null); // dataURL for preview
  const [appointment, setAppointment] = useState(null); // { scheduled_at, mode, notes }
  const [showAppt, setShowAppt] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [zipLoading, setZipLoading] = useState(false);
  const [raffles, setRaffles] = useState([]);
  const [raffleId, setRaffleId] = useState('');
  const [raffleTicket, setRaffleTicket] = useState('');
  const fileRef = useRef();
  const fieldsRef = useRef(null);

  // Active raffles assigned to this agent — shown as an optional selector.
  useEffect(() => {
    let alive = true;
    api.get(`/public/${agentRef}/raffles`, { params: { npn } })
      .then(({ data }) => { if (alive) setRaffles(data.raffles || []); })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentRef, npn]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Enter advances to the next field — fewer taps on a tablet.
  const focusNextOnEnter = (e) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    const tag = e.target.tagName;
    if (tag === 'TEXTAREA' || e.target.type === 'date') return;
    e.preventDefault();
    const els = Array.from(
      fieldsRef.current?.querySelectorAll('input, select') || [],
    ).filter((el) => !el.disabled && el.offsetParent !== null);
    const i = els.indexOf(e.target);
    if (i >= 0 && i < els.length - 1) els[i + 1].focus();
  };

  // Progressive US phone formatting: (xxx) xxx-xxxx. Backend stores digits.
  const formatUsPhone = (v) => {
    const d = (v || '').replace(/\D/g, '').slice(0, 10);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  };

  // ZIP → City/State autofill via the free Zippopotam API (US, no key).
  const onZipChange = async (v) => {
    const z = (v || '').replace(/\D/g, '').slice(0, 5);
    upd('zip_code', z);
    if (z.length !== 5) return;
    setZipLoading(true);
    try {
      const r = await fetch(`https://api.zippopotam.us/us/${z}`);
      if (r.ok) {
        const j = await r.json();
        const place = j.places?.[0];
        if (place) {
          setForm((f) => ({
            ...f,
            city: place['place name'] || f.city,
            state: place['state abbreviation'] || f.state,
          }));
        }
      }
    } catch { /* offline / invalid ZIP — leave fields for manual entry */ }
    finally { setZipLoading(false); }
  };

  const onPhotoPick = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error('Photo too large (max 10MB)'); return; }
    const reader = new FileReader();
    reader.onload = () => setPhotoData({ dataUrl: reader.result, contentType: f.type });
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!form.first_name.trim()) { toast.error('First name is required'); return; }
    if (!form.consent_signature.trim()) { toast.error('Please type the client\u2019s name as consent'); return; }
    setSubmitting(true);
    try {
      let photo_storage_key = null;
      if (photoData) {
        const { data: pdata } = await api.post(`/public/${agentRef}/lead-photo`, {
          image_base64: photoData.dataUrl,
          content_type: photoData.contentType,
        }, { params: { npn } });
        photo_storage_key = pdata.storage_key;
      }
      const payload = { ...form, photo_storage_key, appointment: appointment || null, raffle_id: raffleId || null, raffle_ticket_number: (raffleId && raffleTicket.trim()) || null };
      const { data } = await api.post(`/public/${agentRef}/events/${event.event_id}/leads`,
        payload, { params: { npn } });
      onDone(data);
    } catch (err) {
      if (err?.response?.status === 401) onLogout(true);
      else toast.error(err?.response?.data?.detail || 'Failed to save lead');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-4">
      <BrandHeader agentName={agentName} onLogout={() => onLogout(false)} />
      <KioskCard className="p-5 sm:p-6" data-testid="lead-form-step">
        <div className="flex items-center justify-between mb-4 gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Capturing for</div>
            <div className="font-semibold text-[#0f2547] truncate">{event.name}</div>
          </div>
          <button onClick={onBack}
                  className="text-xs text-slate-500 hover:text-[#0f2547] underline whitespace-nowrap"
                  data-testid="lead-form-change-event">
            Change event
          </button>
        </div>

        <div ref={fieldsRef} onKeyDown={focusNextOnEnter} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FieldInput label="First name*" value={form.first_name} onChange={(v) => upd('first_name', v)} testid="lead-first-name" autoFocus />
          <FieldInput label="Last name" value={form.last_name} onChange={(v) => upd('last_name', v)} testid="lead-last-name" />
          <FieldInput label="Phone" value={form.phone} onChange={(v) => upd('phone', formatUsPhone(v))} icon={Phone} testid="lead-phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="(555) 123-4567" />
          <FieldInput label="Email" value={form.email} onChange={(v) => upd('email', v)} icon={Mail} testid="lead-email" type="email" inputMode="email" autoComplete="email" />
          <FieldInput label="Address" value={form.address} onChange={(v) => upd('address', v)} icon={MapPin} testid="lead-address" className="sm:col-span-2" />
          <FieldInput label={zipLoading ? 'ZIP (looking up…)' : 'ZIP'} value={form.zip_code} onChange={onZipChange} testid="lead-zip" inputMode="numeric" maxLength={5} placeholder="ZIP auto-fills city/state" />
          <FieldInput label="City" value={form.city} onChange={(v) => upd('city', v)} testid="lead-city" />
          <FieldInput label="State" value={form.state} onChange={(v) => upd('state', v.toUpperCase())} maxLength={2} testid="lead-state" />
          <FieldInput label="County" value={form.county} onChange={(v) => upd('county', v)} testid="lead-county" />
          <FieldInput label="Date of birth" type="date" value={form.date_of_birth} onChange={(v) => upd('date_of_birth', v)} testid="lead-dob" />
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Preferred language</label>
            <select value={form.preferred_language} onChange={(e) => upd('preferred_language', e.target.value)}
                    className="w-full px-3 py-2.5 rounded-md ring-1 ring-slate-200 focus:ring-2 focus:ring-[#0f2547] focus:outline-none text-base"
                    data-testid="lead-language">
              <option value="en">English</option>
              <option value="es">Español</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Product of interest</label>
            <select value={form.product_of_interest} onChange={(e) => upd('product_of_interest', e.target.value)}
                    className="w-full px-3 py-2.5 rounded-md ring-1 ring-slate-200 focus:ring-2 focus:ring-[#0f2547] focus:outline-none text-base"
                    data-testid="lead-product">
              <option value="">— Select —</option>
              {PRODUCT_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>

        {/* Photo */}
        <div className="mt-4">
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Photo (optional)</label>
          <div className="flex items-center gap-3">
            {photoData ? (
              <img src={photoData.dataUrl} alt="Lead" className="w-16 h-16 rounded-lg object-cover ring-1 ring-slate-200" data-testid="lead-photo-preview" />
            ) : (
              <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400">
                <Camera size={20} />
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*" capture="environment"
                   onChange={onPhotoPick} className="hidden" data-testid="lead-photo-input" />
            <button onClick={() => fileRef.current?.click()}
                    className="px-3 py-2 rounded-md ring-1 ring-slate-300 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-1.5"
                    data-testid="lead-photo-btn">
              <Camera size={14} />
              {photoData ? 'Retake' : 'Take photo / upload'}
            </button>
            {photoData && (
              <button onClick={() => setPhotoData(null)}
                      className="text-xs text-slate-500 hover:text-rose-600">Remove</button>
            )}
          </div>
        </div>

        {/* Appointment */}
        <div className="mt-4">
          <button onClick={() => setShowAppt((v) => !v)}
                  className="text-sm text-[#0f2547] font-semibold flex items-center gap-1"
                  data-testid="lead-appt-toggle">
            <Calendar size={14} /> {showAppt ? 'Remove appointment' : 'Schedule an appointment'}
          </button>
          {showAppt && (
            <div className="mt-2 p-3 rounded-lg bg-slate-50 ring-1 ring-slate-200 space-y-2" data-testid="lead-appt-form">
              <input type="datetime-local"
                     value={appointment?.scheduled_at?.slice(0, 16) || ''}
                     onChange={(e) => setAppointment({ ...(appointment || {}), scheduled_at: new Date(e.target.value).toISOString(), mode: appointment?.mode || 'in_person' })}
                     className="w-full px-3 py-2 rounded-md ring-1 ring-slate-200 text-sm"
                     data-testid="lead-appt-datetime" />
              <div className="flex gap-2">
                {['in_person', 'phone', 'virtual'].map((m) => (
                  <button key={m}
                          onClick={() => setAppointment({ ...(appointment || {}), mode: m })}
                          className={`px-3 py-1.5 rounded-md text-xs font-semibold capitalize ${
                            (appointment?.mode || 'in_person') === m
                              ? 'bg-[#0f2547] text-white' : 'bg-white ring-1 ring-slate-200 text-slate-600'
                          }`}
                          data-testid={`appt-mode-${m}`}>
                    {m.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Raffle — optional prize-draw entry. When selected the lead gets
            the raffle confirmation SMS instead of the welcome message. */}
        {raffles.length > 0 && (
          <div className="mt-4" data-testid="lead-raffle-block">
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
              Enter a raffle (optional)
            </label>
            <select
              value={raffleId}
              onChange={(e) => setRaffleId(e.target.value)}
              className="w-full px-3 py-2 rounded-md ring-1 ring-slate-200 focus:ring-2 focus:ring-[#0f2547] focus:outline-none text-sm bg-white"
              data-testid="lead-raffle-select"
            >
              <option value="">— No raffle —</option>
              {raffles.map((r) => (
                <option key={r.raffle_id} value={r.raffle_id}>{r.name} — {r.prize}</option>
              ))}
            </select>
            {raffleId && (
              <>
                <input
                  value={raffleTicket}
                  onChange={(e) => setRaffleTicket(e.target.value)}
                  placeholder="Ticket number (optional)"
                  className="mt-2 w-full px-3 py-2 rounded-md ring-1 ring-slate-200 focus:ring-2 focus:ring-[#0f2547] focus:outline-none text-sm bg-white"
                  data-testid="lead-raffle-ticket"
                />
                <p className="mt-1 text-xs text-amber-700">
                  The lead will receive a raffle confirmation SMS in their preferred language{raffleTicket.trim() ? ', including their ticket number' : ''}.
                </p>
              </>
            )}
          </div>
        )}

        {/* Notes — coverage details, doctors, meds, conditions… */}
        <div className="mt-4">
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
            Notes
          </label>
          <textarea
            value={form.notes}
            onChange={(e) => upd('notes', e.target.value)}
            rows={3}
            placeholder="Current coverage, doctors, medications, conditions, best time to call — anything that helps you work this lead later."
            className="w-full px-3 py-2 rounded-md ring-1 ring-slate-200 focus:ring-2 focus:ring-[#0f2547] focus:outline-none text-sm resize-y min-h-[70px]"
            data-testid="lead-notes"
          />
        </div>

        {/* Safety word + Consent */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 flex items-center gap-1">
              <Sparkles size={11} className="text-[#8a6510]" /> Safety word
            </label>
            <input value={form.safety_word} onChange={(e) => upd('safety_word', e.target.value)}
                   placeholder="A word only you and the lead know"
                   className="w-full px-3 py-2 rounded-md ring-1 ring-slate-200 focus:ring-2 focus:ring-[#0f2547] focus:outline-none text-sm"
                   maxLength={40}
                   data-testid="lead-safety-word" />
            <p className="mt-1 text-[10px] text-slate-500">Used to confirm future messages are really from you.</p>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 flex items-center gap-1">
              <ShieldCheck size={11} className="text-emerald-600" /> Consent signature*
            </label>
            <input value={form.consent_signature} onChange={(e) => upd('consent_signature', e.target.value)}
                   placeholder="Type client's full name"
                   className="w-full px-3 py-2 rounded-md ring-1 ring-slate-200 focus:ring-2 focus:ring-[#0f2547] focus:outline-none text-sm italic"
                   style={{ fontFamily: 'cursive' }}
                   maxLength={120}
                   data-testid="lead-consent" />
            <p className="mt-1 text-[10px] text-slate-500">Client typing their name confirms they consent to being contacted.</p>
          </div>
        </div>

        {/* SMS opt-in */}
        <label className={`mt-4 flex items-start gap-2 p-3 rounded-lg ring-1 ${
          canSms ? 'ring-slate-200 hover:bg-slate-50 cursor-pointer' : 'ring-slate-200 bg-slate-50/40 cursor-not-allowed opacity-60'
        }`}>
          <input type="checkbox" disabled={!canSms}
                 checked={form.sms_opt_in}
                 onChange={(e) => upd('sms_opt_in', e.target.checked)}
                 className="mt-0.5"
                 data-testid="lead-sms-optin" />
          <div className="text-xs text-slate-700">
            <div className="font-semibold text-[#0f2547]">Send SMS welcome from this agent</div>
            <div className="text-slate-500">
              {canSms
                ? 'Lead will receive a text message confirming your contact right after submit.'
                : 'Disabled — your account does not have SMS lead outreach permission yet.'}
            </div>
          </div>
        </label>

        <button
          onClick={submit}
          disabled={submitting}
          className="mt-5 w-full py-3 rounded-lg bg-[#0f2547] text-white font-semibold hover:bg-[#14335f] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          data-testid="lead-form-submit"
        >
          {submitting ? <Loader2 className="animate-spin" size={18} /> : <><CheckCircle2 size={16} /> Save & continue</>}
        </button>
      </KioskCard>
    </div>
  );
}

function FieldInput({ label, value, onChange, testid, icon: Icon, className = '', ...props }) {
  return (
    <div className={className}>
      <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">{label}</label>
      <div className="relative">
        {Icon && <Icon size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />}
        <input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full ${Icon ? 'pl-9' : 'pl-3'} pr-3 py-2.5 rounded-md ring-1 ring-slate-200 focus:ring-2 focus:ring-[#0f2547] focus:outline-none text-base`}
          data-testid={testid}
          {...props}
        />
      </div>
    </div>
  );
}

// ── STEP 4 — Success + next actions ────────────────────────
function SuccessStep({ result, event, agentName, onAddAnother, onChangeEvent, onLogout }) {
  const dedupBadge = result?.dedup === 'linked'
    ? { bg: 'bg-sky-100', txt: 'text-sky-800', label: 'Lead linked to event' }
    : { bg: 'bg-emerald-100', txt: 'text-emerald-800', label: 'New lead captured' };
  return (
    <div className="space-y-4">
      <BrandHeader agentName={agentName} onLogout={() => onLogout(false)} />
      <KioskCard className="p-8 text-center" data-testid="success-step">
        <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
          <CheckCircle2 size={32} className="text-emerald-600" />
        </div>
        <h2 className="text-xl font-heading font-bold text-[#0f2547] mb-1">
          {result?.dedup === 'linked' ? 'Lead re-linked!' : 'Lead captured!'}
        </h2>
        <p className="text-sm text-slate-600 mb-3">
          Saved under <span className="font-semibold text-[#0f2547]">{event.name}</span>
        </p>
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${dedupBadge.bg} ${dedupBadge.txt} mb-6`} data-testid="success-dedup">
          {dedupBadge.label}
        </span>
        {result?.sms_sent && (
          <div className="text-xs text-emerald-700 mb-4 flex items-center gap-1 justify-center">
            <ShieldCheck size={12} /> SMS sent to lead
          </div>
        )}
        {result?.appointment_id && (
          <div className="text-xs text-sky-700 mb-4 flex items-center gap-1 justify-center">
            <Calendar size={12} /> Appointment scheduled
          </div>
        )}
        <div className="flex flex-col sm:flex-row gap-2 mt-4">
          <button onClick={onAddAnother}
                  className="flex-1 py-3 rounded-lg bg-[#0f2547] text-white font-semibold hover:bg-[#14335f] transition-colors flex items-center justify-center gap-2"
                  data-testid="success-add-another">
            <Plus size={16} /> Capture another lead
          </button>
          <button onClick={onChangeEvent}
                  className="flex-1 py-3 rounded-lg ring-1 ring-slate-200 text-slate-700 font-semibold hover:bg-slate-50 transition-colors"
                  data-testid="success-change-event">
            Change event
          </button>
        </div>
      </KioskCard>
    </div>
  );
}

// ── Page shell ─────────────────────────────────────────────

// Kiosk-scoped error boundary: a public, unauthenticated page used live at
// events. A single bad record must NOT white-screen the agent nor send them
// to /dashboard (login). Instead we show a friendly "start over" and surface
// the error message so support can act on it.
class KioskErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, message: (error && error.message) || String(error) };
  }
  componentDidCatch(error, info) {
    console.error('AgentEventsKiosk error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen w-full py-10 px-4 flex items-start justify-center"
             style={{ background: 'linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)' }}
             data-testid="kiosk-error">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-xl ring-1 ring-slate-200 p-6 text-center">
            <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-7 h-7 text-amber-600" />
            </div>
            <h2 className="text-lg font-heading font-bold text-[#0f2547]">Something interrupted the kiosk</h2>
            <p className="text-sm text-slate-600 mt-2">
              Please start over and re-enter your NPN. If this keeps happening, share
              this message with your KR admin.
            </p>
            {this.state.message && (
              <p className="mt-3 text-xs font-mono text-slate-500 bg-slate-50 rounded-md p-2 break-words">
                {this.state.message}
              </p>
            )}
            <button
              onClick={() => { window.location.href = '/agentevents'; }}
              className="mt-5 w-full py-3 rounded-lg bg-[#0f2547] text-white font-semibold hover:bg-[#14335f] transition-colors flex items-center justify-center gap-2"
              data-testid="kiosk-error-restart"
            >
              <RefreshCw size={16} /> Start over
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function AgentEventsKioskPage() {
  const [state, setState] = useState({
    step: 'lookup', session: null, agentRef: null, npn: null,
    agentName: '', canSms: false, event: null, lastResult: null,
  });

  // Hub deep-link: /agentevents?ref=...&session=...&npn=...&event=...&event_name=...&event_date=...
  // Skips the lookup step. If event query is present, we jump straight
  // to the lead-capture form for that event.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    const session = params.get('session');
    const npn = params.get('npn');
    if (!ref || !session || !npn) return;
    const name = params.get('agent_name') || 'Agent';
    const canSms = params.get('can_sms') === '1';
    const eventId = params.get('event');
    const patch = {
      session, agentRef: ref, npn, agentName: name, canSms,
      step: eventId ? 'lead' : 'events',
      event: eventId ? {
        event_id: eventId,
        name: params.get('event_name') || 'Event',
        date: params.get('event_date') || '',
        kind: params.get('event_kind') || 'event',
      } : null,
    };
    setState((s) => ({ ...s, ...patch }));
    // Scrub the URL so refresh doesn't leak the session token in history.
    window.history.replaceState({}, '', '/agentevents');
  }, []);

  const logout = (expired = false) => {
    if (expired) toast.info('Kiosk session expired — please re-enter your NPN.');
    setState({ step: 'lookup', session: null, agentRef: null, npn: null,
               agentName: '', canSms: false, event: null, lastResult: null });
  };

  return (
    <div className="min-h-screen w-full py-6 px-4"
         style={{ background: 'radial-gradient(1200px 500px at 0% 0%, rgba(15,37,71,0.08), transparent 60%), radial-gradient(900px 400px at 100% 100%, rgba(245,193,79,0.10), transparent 60%), linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)' }}
         data-testid="agent-events-kiosk">
      <Toaster richColors position="top-center" />
      <div className="max-w-3xl mx-auto space-y-4">
        <KioskErrorBoundary>
        {state.step === 'lookup' && (
          <LookupStep onSignedIn={(data) => setState((s) => ({
            ...s, step: 'events', session: data.session, agentRef: data.agent_ref,
            npn: data.npn, agentName: `${data.first_name} ${data.last_name || ''}`.trim(),
            canSms: data.can_sms_leads,
          }))} />
        )}
        {state.step === 'events' && (
          <EventListStep
            session={state.session}
            agentRef={state.agentRef}
            npn={state.npn}
            agentName={state.agentName}
            canSms={state.canSms}
            onPicked={(event) => setState((s) => ({ ...s, step: 'lead', event }))}
            onLogout={logout}
          />
        )}
        {state.step === 'lead' && (
          <LeadFormStep
            session={state.session}
            agentRef={state.agentRef}
            npn={state.npn}
            agentName={state.agentName}
            canSms={state.canSms}
            event={state.event}
            onDone={(result) => setState((s) => ({ ...s, step: 'success', lastResult: result }))}
            onBack={() => setState((s) => ({ ...s, step: 'events' }))}
            onLogout={logout}
          />
        )}
        {state.step === 'success' && (
          <SuccessStep
            result={state.lastResult}
            event={state.event}
            agentName={state.agentName}
            onAddAnother={() => setState((s) => ({ ...s, step: 'lead', lastResult: null }))}
            onChangeEvent={() => setState((s) => ({ ...s, step: 'events', lastResult: null }))}
            onLogout={logout}
          />
        )}
        </KioskErrorBoundary>
      </div>
    </div>
  );
}
